import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { FormInputDate } from '../../../../form-components/FormInputDate';

import LockerSelectModal from './lockerSelectModal/LockerSelectModal';
import LockerTicketSelectModal from './lockerTicketSelectModal/LockerTicketSelectModal';
import { getMemberLockerTickets } from '../../../../api/ticket';
import { getBranchProductsById } from '../../../../api/point';
import { errorMessages } from '../../../../utils/methods';

import {
  FormInputWrapper,
  dateInputStyleFlex,
  FormButton,
  FormButtonWrapper,
  LockerExtendButton,
  ErrorFieldWrapper,
  DateSeparator,
  ContentSection,
  FormFieldGroup,
  FormFieldGroupHalf,
  FormFieldRow,
  FormFieldLabel,
} from './EnterSaleInfoModalStyled';

const EnterSaleInfoModalPaymentSection = ({
  control,
  setValue,
  watch,
  activeTab,
  productType,
  errorFields,
  setErrorFields,
  branchId,
  memberId,
  userType, // 'member' | 'temporary' — 잠재회원이면 my-locker 호출 스킵
  getProductPrice,
  // 회원상세 라커 카드 [연장]으로 진입한 경우 — 해당 자리를 미리 선택하고 기간을 세팅한다.
  initialLockerExtension = null,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);

  // 상품 목록 조회 (연장 시 상품 매칭용)
  const { data: productList } = useQuery({
    queryKey: ['EnterSaleProductList', user?.branch?.branchId],
    queryFn: () => getBranchProductsById(user?.token, user?.branch?.branchId, 'page=1&limit=1000000'),
    enabled: !!user?.token && !!user?.branch?.branchId,
  });

  // 잠재회원(userType === 'temporary')은 아직 정식 멤버로 등록되지 않아
  // my-locker가 항상 404를 반환하므로 호출 자체를 스킵한다.
  const isRealMember = userType === 'member';

  // 회원의 라커 이용권 조회 (연장 버튼 활성화 여부 확인용)
  const { data: memberLockerTicketsData, isLoading: isLockerTicketsLoading } = useQuery({
    queryKey: ['memberLockerTickets', branchId, memberId],
    queryFn: () => getMemberLockerTickets(branchId, memberId, user.token),
    enabled: !!branchId && !!memberId && isRealMember && !!user?.token,
  });

  // 라커 이용권이 있는지 확인
  const hasLockerTickets = memberLockerTicketsData && memberLockerTicketsData.length > 0;

  const [lockerModalOpen, setLockerModalOpen] = useState(false);
  const [lockerTicketModalOpen, setLockerTicketModalOpen] = useState(false);
  const [lockerTickets, setLockerTickets] = useState([]);
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [selectedLockerTicket, setSelectedLockerTicket] = useState(null);
  const [isLockerButtonHovered, setIsLockerButtonHovered] = useState(false);

  // 한 자리의 마지막 이용 종료일 — 연장은 항상 여기에 이어 붙인다.
  // 회원상세/라커관리 [연장 추가] 와 판매등록 [라커연장] 이 같은 계산을 쓰도록 여기 한 곳에 둔다.
  const lastEndDateOfSpot = lockerId => {
    const ends = (memberLockerTicketsData ?? [])
      .filter(ticket => ticket.locker?.lockerId === lockerId && ticket.expireDate)
      .map(ticket => ticket.expireDate)
      .sort();
    return ends.length > 0 ? ends[ends.length - 1] : null;
  };

  // 회원상세·라커관리 라커 카드 [연장 추가] 진입 — 그 자리를 미리 선택하고,
  // 자리의 마지막 이용 종료일 다음 날을 시작일로 잡는다(이용권이 여러 개여도 맨 뒤 기준).
  useEffect(() => {
    if (!initialLockerExtension || activeTab !== 'product' || productType !== 'LOCKER') return;
    setSelectedLocker({
      lockerId: initialLockerExtension.lockerId,
      lockerAreaName: initialLockerExtension.lockerAreaName,
      lockerNumber: initialLockerExtension.lockerNumber,
    });
    setValue('lockerId', initialLockerExtension.lockerId);
    // 넘어온 카드의 만료일이 아니라 자리 전체의 마지막 종료일을 쓴다(목록 로딩 후 확정).
    const baseEnd = lastEndDateOfSpot(initialLockerExtension.lockerId) ?? initialLockerExtension.expireDate;
    if (baseEnd && !dayjs(baseEnd).isBefore(dayjs(), 'day')) {
      applyExtensionPeriod(baseEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLockerExtension, activeTab, productType, memberLockerTicketsData]);

  // 시작일 변경 시 종료일 자동 재계산
  const watchedStartDate = watch('courseStartDate');
  const watchedProductId = watch('productId');

  useEffect(() => {
    if (!watchedStartDate || !watchedProductId || !productList?.content) return;
    if (activeTab !== 'product') return;
    if (productType !== 'LOCKER' && productType !== 'PERIOD') return;

    const selectedProduct = productList.content.find(item => item.productId === watchedProductId);
    if (!selectedProduct?.periodValue || !selectedProduct?.periodType) return;

    let endDate;
    if (selectedProduct.periodType === 'MONTH') {
      endDate = dayjs(watchedStartDate).add(selectedProduct.periodValue, 'month').subtract(1, 'day').toDate();
    } else if (selectedProduct.periodType === 'DAY') {
      endDate = dayjs(watchedStartDate).add(selectedProduct.periodValue, 'day').subtract(1, 'day').toDate();
    }

    if (endDate) {
      setValue('courseEndDate', endDate);
    }
  }, [watchedStartDate, watchedProductId, productList, activeTab, productType, setValue]);

  const handleLockerSelect = locker => {
    setSelectedLocker(locker);
    setValue('lockerId', locker.lockerId);

    // 본인이 점유 중인 자리를 고르면 연장 — 기존 기간이 아직 남아 있으면 시작일 기본값(오늘)이
    // 그대로 겹침 거부(406)를 맞으므로, 자리 만료일 다음 날로 이용기간을 미리 세팅한다.
    // 만료가 이미 지난 자리(연체)는 오늘 시작이 겹치지 않아 그대로 둔다.
    const isOwnSpot = memberId && locker.memberId === memberId;
    const baseEnd = lastEndDateOfSpot(locker.lockerId) ?? locker.expireDate;
    const spotStillActive = baseEnd && !dayjs(baseEnd).isBefore(dayjs(), 'day');
    if (isOwnSpot && spotStillActive) {
      applyExtensionPeriod(baseEnd);
    }
  };

  // 기준 만료일 다음 날을 시작일로, 선택된 상품의 기간으로 종료일을 세팅한다.
  // (라커연장 버튼 경로와 자리 직접 선택 경로가 공유)
  const applyExtensionPeriod = baseEndDate => {
    const startDate = dayjs(baseEndDate).add(1, 'day').toDate();
    setValue('courseStartDate', startDate);
    setErrorFields(prev => ({ ...prev, courseStartDate: false }));

    const selectedProductId = watch('productId');
    if (selectedProductId && productList?.content) {
      const selectedProduct = productList.content.find(item => item.productId === selectedProductId);
      if (selectedProduct?.periodValue && selectedProduct?.periodType) {
        let endDate;
        if (selectedProduct.periodType === 'MONTH') {
          endDate = dayjs(startDate).add(selectedProduct.periodValue, 'month').subtract(1, 'day').toDate();
        } else if (selectedProduct.periodType === 'DAY') {
          endDate = dayjs(startDate).add(selectedProduct.periodValue, 'day').subtract(1, 'day').toDate();
        }
        if (endDate) {
          setValue('courseEndDate', endDate);
          setErrorFields(prev => ({ ...prev, courseEndDate: false }));
        }
      }
    }
  };

  const handleExtendClick = () => {
    if (!branchId || !memberId) {
      toast.error(t('지점 또는 회원 정보가 없습니다.'));
      return;
    }

    if (!memberLockerTicketsData || memberLockerTicketsData.length === 0) {
      toast.error(t('라커 이용권이 없습니다.'));
      return;
    }

    // 이미 조회된 데이터를 lockerAreaName 기준으로 그룹화
    const groupedTickets = groupLockerTicketsByArea(memberLockerTicketsData);
    setLockerTickets(groupedTickets);
    setLockerTicketModalOpen(true);
  };

  // API 응답을 구역 → 자리 → 이용권으로 묶는다.
  // 한 자리에 이용권이 여러 개면 카드 하나에 스택으로 보여주고, 연장은 마지막 기간 뒤에 붙인다.
  const groupLockerTicketsByArea = tickets => {
    if (!tickets || !Array.isArray(tickets)) return [];

    const areas = new Map();
    tickets.forEach(ticket => {
      const areaName = ticket.locker?.lockerAreaName || '미배정 라커';
      const areaId = ticket.locker?.lockerAreaId ?? 'unassigned';
      // 미배정은 자리가 없으므로 이용권 하나가 카드 하나
      const spotKey = ticket.locker?.lockerId ?? `unassigned-${ticket.memberLockerPassId}`;

      if (!areas.has(areaId)) {
        areas.set(areaId, { lockerAreaId: areaId, lockerAreaName: areaName, spots: new Map() });
      }
      const spots = areas.get(areaId).spots;
      if (!spots.has(spotKey)) {
        spots.set(spotKey, {
          spotKey,
          lockerId: ticket.locker?.lockerId ?? null,
          lockerNumber: ticket.locker?.lockerNumber ?? null,
          locker: ticket.locker ?? null,
          passes: [],
        });
      }
      spots.get(spotKey).passes.push({
        ticketId: ticket.memberLockerPassId,
        productName: ticket.lockerPassName,
        startDate: ticket.startDate,
        endDate: ticket.expireDate,
        status: ticket.status,
      });
    });

    return [...areas.values()].map(area => ({
      lockerAreaId: area.lockerAreaId,
      lockerAreaName: area.lockerAreaName,
      spots: [...area.spots.values()].map(spot => {
        const passes = [...spot.passes].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
        return { ...spot, passes, lastEndDate: passes[passes.length - 1]?.endDate ?? null };
      }),
    }));
  };

  // 자리를 고르면 그 자리를 판매 대상으로 잡고, 마지막 이용 기간 다음 날부터 연장한다.
  const handleLockerTicketSelect = spot => {
    setSelectedLockerTicket(spot);

    if (spot.locker) {
      setSelectedLocker({
        lockerId: spot.locker.lockerId,
        lockerAreaName: spot.locker.lockerAreaName,
        lockerNumber: spot.locker.lockerNumber,
      });
      setValue('lockerId', spot.locker.lockerId);
    }

    const baseEnd = spot.lockerId ? lastEndDateOfSpot(spot.lockerId) ?? spot.lastEndDate : spot.lastEndDate;
    if (baseEnd) {
      applyExtensionPeriod(baseEnd);
    }
  };

  return (
    <>
      {/* 락커 선택 (일반상품 탭 & 라커 타입일 때만) */}
      {activeTab === 'product' && productType === 'LOCKER' && (
        <ContentSection>
          <FormFieldGroup>
            <FormFieldLabel>{t('라커 선택')}</FormFieldLabel>
            <FormButtonWrapper>
              {/* 라커 선택은 옵션 — 미선택으로 판매하면 미배정 상태가 되고 회원상세에서 배정한다. */}
              <FormButton
                onClick={() => setLockerModalOpen(true)}
                onMouseEnter={() => setIsLockerButtonHovered(true)}
                onMouseLeave={() => setIsLockerButtonHovered(false)}>
                {selectedLocker
                  ? isLockerButtonHovered
                    ? t('다시선택')
                    : `${selectedLocker.lockerAreaName}•${selectedLocker.lockerNumber}번 라커`
                  : t('선택하기 (선택 안 하면 미배정)')}
              </FormButton>
              <LockerExtendButton onClick={handleExtendClick} disabled={isLockerTicketsLoading || !hasLockerTickets}>
                {isLockerTicketsLoading ? '...' : t('라커연장')}
              </LockerExtendButton>
            </FormButtonWrapper>
          </FormFieldGroup>

          {/* 이용 기간 */}
          <FormFieldGroup>
            <FormFieldLabel>{t('이용기간')}</FormFieldLabel>
            <FormInputWrapper>
              <ErrorFieldWrapper hasError={errorFields.courseStartDate}>
                <FormInputDate
                  name="courseStartDate"
                  control={control}
                  rules={{ required: true }}
                  placeholder={t('오늘 날짜')}
                  onChange={() => setErrorFields(prev => ({ ...prev, courseStartDate: false }))}
                  renderInput={params => (
                    <TextField color="info" size="small" variant="outlined" {...params} sx={dateInputStyleFlex} />
                  )}
                />
              </ErrorFieldWrapper>
              <DateSeparator>~</DateSeparator>
              <ErrorFieldWrapper hasError={errorFields.courseEndDate}>
                <FormInputDate
                  name="courseEndDate"
                  control={control}
                  rules={{ required: true }}
                  placeholder={t('자동')}
                  onChange={() => setErrorFields(prev => ({ ...prev, courseEndDate: false }))}
                  renderInput={params => (
                    <TextField color="info" size="small" variant="outlined" {...params} sx={dateInputStyleFlex} />
                  )}
                />
              </ErrorFieldWrapper>
            </FormInputWrapper>
          </FormFieldGroup>
        </ContentSection>
      )}

      {/* 이용 기간 (기간 상품에만 표시 - 라커 제외) */}
      {activeTab === 'product' && productType === 'PERIOD' && (
        <ContentSection>
          <FormFieldGroup>
            <FormFieldLabel>{t('이용기간')}</FormFieldLabel>
            <FormInputWrapper>
              <ErrorFieldWrapper hasError={errorFields.courseStartDate}>
                <FormInputDate
                  name="courseStartDate"
                  control={control}
                  rules={{ required: true }}
                  placeholder={t('오늘 날짜')}
                  onChange={() => setErrorFields(prev => ({ ...prev, courseStartDate: false }))}
                  renderInput={params => (
                    <TextField color="info" size="small" variant="outlined" {...params} sx={dateInputStyleFlex} />
                  )}
                />
              </ErrorFieldWrapper>
              <DateSeparator>~</DateSeparator>
              <ErrorFieldWrapper hasError={errorFields.courseEndDate}>
                <FormInputDate
                  name="courseEndDate"
                  control={control}
                  rules={{ required: true }}
                  placeholder={t('자동')}
                  onChange={() => setErrorFields(prev => ({ ...prev, courseEndDate: false }))}
                  renderInput={params => (
                    <TextField color="info" size="small" variant="outlined" {...params} sx={dateInputStyleFlex} />
                  )}
                />
              </ErrorFieldWrapper>
            </FormInputWrapper>
          </FormFieldGroup>
        </ContentSection>
      )}

      {/* 락커 선택 모달 — memberId 를 넘기면 본인 점유 자리(연장)도 선택 가능 */}
      <LockerSelectModal
        memberId={userType === 'member' ? memberId : null}
        open={lockerModalOpen}
        onClose={() => setLockerModalOpen(false)}
        onSelect={handleLockerSelect}
        initialLocker={selectedLocker}
      />

      {/* 연장 라커 선택 모달 */}
      <LockerTicketSelectModal
        open={lockerTicketModalOpen}
        onClose={() => setLockerTicketModalOpen(false)}
        onSelect={handleLockerTicketSelect}
        lockerTickets={lockerTickets}
      />

    </>
  );
};

export default EnterSaleInfoModalPaymentSection;
